import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { z } from "zod";
import { api, ApiError } from "@/lib/api";
import {
  INTAKE_STEPS_BY_TYPE,
} from "@alice/shared";
import type { PersonType } from "@alice/shared";

// ---------------------------------------------------------------------------
// Dropdown option types
// ---------------------------------------------------------------------------

export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownConfig {
  genders: DropdownOption[];
  races: DropdownOption[];
  ethnicities: DropdownOption[];
  languages: DropdownOption[];
  maritalStatuses: DropdownOption[];
  educationLevels: DropdownOption[];
  employmentStatuses: DropdownOption[];
  referralSources: DropdownOption[];
  abuseTypes: DropdownOption[];
  relationships: DropdownOption[];
  housingStatuses: DropdownOption[];
  incomeSourceTypes: DropdownOption[];
  familyTypes: DropdownOption[];
  victimTypes: DropdownOption[];
  sites: DropdownOption[];
  programs: DropdownOption[];
  advocates: DropdownOption[];
  usStates: DropdownOption[];
}

export interface CustomField {
  id: string;
  name: string;
  label: string;
  type: "text" | "textarea" | "select" | "checkbox" | "number" | "date";
  required: boolean;
  options?: DropdownOption[];
  context: string;
}

// ---------------------------------------------------------------------------
// Extended intake form schema (beyond shared createPersonSchema)
// ---------------------------------------------------------------------------

export const intakeFormSchema = z.object({
  // Step metadata
  personType: z.enum(["shelter", "outreach", "hotline", "child"]),
  intakeDate: z.string().min(1, "Intake date is required"),
  intakeTime: z.string().optional(),

  // Step 1 - Demographics
  firstName: z.string().min(1, "First name is required").max(100),
  lastName: z.string().min(1, "Last name is required").max(100),
  middleName: z.string().max(100).optional(),
  preferredName: z.string().max(100).optional(),
  aliases: z.array(z.string()).optional(),
  idNumber: z.string().optional(),
  ssn: z.string().optional(),
  dateOfBirth: z.string().optional(),
  gender: z.string().optional(),
  race: z.string().optional(),
  ethnicity: z.string().optional(),
  primaryLanguage: z.string().optional(),
  isInterpreterNeeded: z.boolean().optional(),
  maritalStatus: z.string().optional(),
  educationLevel: z.string().optional(),

  // Address
  address: z.string().max(255).optional(),
  addressApt: z.string().max(50).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(2).optional(),
  zipCode: z.string().optional(),
  county: z.string().max(100).optional(),

  // Phones
  phoneHome: z.string().optional(),
  phoneWork: z.string().optional(),
  phoneMobile: z.string().optional(),
  phoneEmergency: z.string().optional(),
  primaryPhone: z.enum(["home", "work", "mobile", "emergency"]).optional(),
  safePhoneHome: z.boolean().optional(),
  safePhoneWork: z.boolean().optional(),
  safePhoneMobile: z.boolean().optional(),

  email: z.string().email("Invalid email").max(255).optional().or(z.literal("")),

  // Intake details
  siteId: z.string().optional(),
  programId: z.string().optional(),
  advocateId: z.string().optional(),
  referralSource: z.string().optional(),

  // Victim/housing
  victimType: z.string().optional(),
  abuseStatus: z.string().optional(),
  housingStatus: z.string().optional(),
  priorLivingSituation: z.string().optional(),
  monthlyIncome: z.number().min(0).optional().nullable(),
  annualIncome: z.number().min(0).optional().nullable(),
  incomeSources: z.array(z.string()).optional(),
  employmentStatus: z.string().optional(),
  householdSize: z.number().int().min(0).optional().nullable(),
  familyType: z.string().optional(),
  numberInShelter: z.number().int().min(0).optional().nullable(),
  urbanRural: z.enum(["urban", "rural", "suburban"]).optional(),

  // Step 2 - Background
  backgroundItems: z.array(z.string()).optional(),
  chemicalDependence: z.string().max(2000).optional(),
  chemicalDependenceSensitive: z.boolean().optional(),
  medications: z.string().max(2000).optional(),
  medicationsSensitive: z.boolean().optional(),
  disabilities: z.array(z.string()).optional(),
  hasDisability: z.boolean().optional(),
  disabilityDetails: z.string().max(1000).optional(),
  abuseTypes: z.array(z.string()).optional(),
  weaponsUsed: z.string().optional(),
  weaponsThreatened: z.string().optional(),
  lengthWithAbuser: z.string().optional(),
  plansAbuser: z.string().max(2000).optional(),
  plansChildren: z.string().max(2000).optional(),
  plansIncome: z.string().max(2000).optional(),
  presentingSituation: z.string().max(5000).optional(),
  relationshipToAbuser: z.string().optional(),
  isAbuseCurrent: z.boolean().optional(),
  abuseStartDate: z.string().optional(),
  hasProtectionOrder: z.boolean().optional(),
  protectionOrderExpiration: z.string().optional(),
  safetyPlanCompleted: z.boolean().optional(),
  dangerAssessmentScore: z.number().min(0).max(100).optional().nullable(),
  isHighDanger: z.boolean().optional(),
  hasChildren: z.boolean().optional(),
  numberOfChildren: z.number().int().min(0).optional().nullable(),
  isPregnant: z.boolean().optional(),
  isVeteran: z.boolean().optional(),
  isHomeless: z.boolean().optional(),

  // Step 3 - Additional
  photoUrl: z.string().optional(),
  narrativeMemo: z.string().max(10000).optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),

  // Injury documentation (body map)
  isInjured: z.enum(["yes", "no", "na"]).optional(),
  conditionWorsened: z.enum(["yes", "no", "na"]).optional(),
  injuries: z.array(z.object({
    id: z.string(),
    side: z.enum(["front", "back"]),
    x: z.number(),
    y: z.number(),
    region: z.string(),
    injuryType: z.enum(["abrasion", "bruise", "broken_bone", "puncture", "other"]),
    notes: z.string().optional(),
  })).optional(),

  // Step 4 - Mental Health
  mentalHealthDiagnosis: z.string().max(1000).optional(),
  isSubstanceAbuse: z.boolean().optional(),
  substanceAbuseDetails: z.string().max(1000).optional(),
  isSuicidal: z.boolean().optional(),
  suicidalDetails: z.string().max(1000).optional(),
  releaseRecord: z.boolean().optional(),
  mentalHealthNarrative: z.string().max(5000).optional(),

  // Child specific
  parentPersonId: z.string().optional(),
  schoolName: z.string().max(200).optional(),
  gradeLevel: z.string().max(20).optional(),
  pediatrician: z.string().max(200).optional(),
  custodyStatus: z.string().max(200).optional(),

  // Shelter specific
  bedId: z.string().optional(),
  expectedDepartureDate: z.string().optional(),
});

export type IntakeFormData = z.infer<typeof intakeFormSchema>;

// ---------------------------------------------------------------------------
// Step validation schemas (subsets for per-step validation)
// ---------------------------------------------------------------------------

const step1Schema = intakeFormSchema.pick({
  firstName: true,
  lastName: true,
  intakeDate: true,
});

const step2Schema = z.object({});
const step3Schema = z.object({});
const step4Schema = z.object({});

const stepSchemas = [step1Schema, step2Schema, step3Schema, step4Schema] as const;

// ---------------------------------------------------------------------------
// Local storage draft
// ---------------------------------------------------------------------------

const DRAFT_KEY = "alice_intake_draft";

function saveDraft(data: Partial<IntakeFormData>) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
  } catch {
    // Quota exceeded or private browsing
  }
}

function loadDraft(): Partial<IntakeFormData> | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<IntakeFormData>;
  } catch {
    return null;
  }
}

function clearDraft() {
  localStorage.removeItem(DRAFT_KEY);
}

// ---------------------------------------------------------------------------
// Default form values
// ---------------------------------------------------------------------------

function getDefaultValues(): IntakeFormData {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10);
  const timeStr = now.toTimeString().slice(0, 5);

  return {
    personType: "shelter",
    intakeDate: dateStr,
    intakeTime: timeStr,
    firstName: "",
    lastName: "",
    middleName: "",
    preferredName: "",
    aliases: [],
    idNumber: "",
    ssn: "",
    dateOfBirth: "",
    gender: "",
    race: "",
    ethnicity: "",
    primaryLanguage: "",
    isInterpreterNeeded: false,
    maritalStatus: "",
    educationLevel: "",
    address: "",
    addressApt: "",
    city: "",
    state: "",
    zipCode: "",
    county: "",
    phoneHome: "",
    phoneWork: "",
    phoneMobile: "",
    phoneEmergency: "",
    primaryPhone: "home",
    safePhoneHome: true,
    safePhoneWork: true,
    safePhoneMobile: true,
    email: "",
    siteId: "",
    programId: "",
    advocateId: "",
    referralSource: "",
    victimType: "",
    abuseStatus: "",
    housingStatus: "",
    priorLivingSituation: "",
    monthlyIncome: undefined,
    annualIncome: undefined,
    incomeSources: [],
    employmentStatus: "",
    householdSize: undefined,
    familyType: "",
    numberInShelter: undefined,
    urbanRural: undefined,
    backgroundItems: [],
    chemicalDependence: "",
    chemicalDependenceSensitive: false,
    medications: "",
    medicationsSensitive: false,
    disabilities: [],
    hasDisability: false,
    disabilityDetails: "",
    abuseTypes: [],
    weaponsUsed: "",
    weaponsThreatened: "",
    lengthWithAbuser: "",
    plansAbuser: "",
    plansChildren: "",
    plansIncome: "",
    presentingSituation: "",
    relationshipToAbuser: "",
    isAbuseCurrent: false,
    abuseStartDate: "",
    hasProtectionOrder: false,
    protectionOrderExpiration: "",
    safetyPlanCompleted: false,
    dangerAssessmentScore: undefined,
    isHighDanger: false,
    hasChildren: false,
    numberOfChildren: undefined,
    isPregnant: false,
    isVeteran: false,
    isHomeless: false,
    photoUrl: "",
    narrativeMemo: "",
    customFields: {},
    mentalHealthDiagnosis: "",
    isSubstanceAbuse: false,
    substanceAbuseDetails: "",
    isSuicidal: false,
    suicidalDetails: "",
    releaseRecord: false,
    mentalHealthNarrative: "",
    parentPersonId: "",
    schoolName: "",
    gradeLevel: "",
    pediatrician: "",
    custodyStatus: "",
    bedId: "",
    expectedDepartureDate: "",
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseIntakeFormReturn {
  form: UseFormReturn<IntakeFormData>;
  currentStep: number;
  totalSteps: number;
  steps: readonly number[];
  canGoNext: boolean;
  canGoBack: boolean;
  goNext: () => Promise<boolean>;
  goBack: () => void;
  goToStep: (step: number) => void;
  saveIncomplete: () => Promise<void>;
  submit: () => Promise<void>;
  isSubmitting: boolean;
  submitError: string | null;
  dropdowns: DropdownConfig | undefined;
  isLoadingDropdowns: boolean;
  customFields: CustomField[];
  isLoadingCustomFields: boolean;
  personType: PersonType;
  setPersonType: (type: PersonType) => void;
  hasDraft: boolean;
  clearFormDraft: () => void;
  ssnDuplicateWarning: string | null;
  checkSsnDuplicate: (ssn: string) => Promise<void>;
  lookupZipCode: (zip: string) => Promise<void>;
}

export function useIntakeForm(): UseIntakeFormReturn {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Check for saved draft
  const draft = useMemo(() => loadDraft(), []);
  const hasDraft = draft !== null;

  const defaults = useMemo(() => {
    const base = getDefaultValues();
    if (draft) return { ...base, ...draft };
    return base;
  }, [draft]);

  const form = useForm<IntakeFormData>({
    resolver: zodResolver(intakeFormSchema),
    defaultValues: defaults,
    mode: "onBlur",
  });

  const personType = form.watch("personType") as PersonType;
  const steps = useMemo(
    () => INTAKE_STEPS_BY_TYPE[personType] ?? [1, 2, 3, 4],
    [personType],
  );
  const totalSteps = steps.length;

  const [stepIndex, setStepIndex] = useState(0);
  const currentStep = steps[stepIndex] ?? 1;

  const canGoBack = stepIndex > 0;
  const canGoNext = stepIndex < totalSteps - 1;

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [ssnDuplicateWarning, setSsnDuplicateWarning] = useState<string | null>(null);

  // Auto-save draft on form change
  useEffect(() => {
    const subscription = form.watch((data) => {
      saveDraft(data as Partial<IntakeFormData>);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Default dropdown options — always available as baseline
  const defaultDropdowns: DropdownConfig = {
    genders: [
      { value: "female", label: "Female" },
      { value: "male", label: "Male" },
      { value: "transgender_female", label: "Transgender Female" },
      { value: "transgender_male", label: "Transgender Male" },
      { value: "non_binary", label: "Non-Binary" },
      { value: "gender_non_conforming", label: "Gender Non-Conforming" },
      { value: "two_spirit", label: "Two-Spirit" },
      { value: "questioning", label: "Questioning" },
      { value: "other", label: "Other" },
      { value: "unknown", label: "Unknown" },
      { value: "declined", label: "Declined to Answer" },
    ],
    races: [
      { value: "american_indian_alaska_native", label: "American Indian/Alaska Native" },
      { value: "asian", label: "Asian" },
      { value: "black_african_american", label: "Black/African American" },
      { value: "native_hawaiian_pacific_islander", label: "Native Hawaiian/Pacific Islander" },
      { value: "white", label: "White" },
      { value: "multiracial", label: "Multiracial" },
      { value: "other", label: "Other" },
      { value: "unknown", label: "Unknown" },
      { value: "declined", label: "Declined to Answer" },
    ],
    ethnicities: [
      { value: "hispanic_latino", label: "Hispanic/Latino" },
      { value: "not_hispanic_latino", label: "Not Hispanic/Latino" },
      { value: "unknown", label: "Unknown" },
      { value: "declined", label: "Declined to Answer" },
    ],
    languages: [
      { value: "english", label: "English" },
      { value: "spanish", label: "Spanish" },
      { value: "french", label: "French" },
      { value: "mandarin", label: "Mandarin Chinese" },
      { value: "cantonese", label: "Cantonese Chinese" },
      { value: "vietnamese", label: "Vietnamese" },
      { value: "korean", label: "Korean" },
      { value: "arabic", label: "Arabic" },
      { value: "tagalog", label: "Tagalog/Filipino" },
      { value: "russian", label: "Russian" },
      { value: "portuguese", label: "Portuguese" },
      { value: "haitian_creole", label: "Haitian Creole" },
      { value: "hindi", label: "Hindi" },
      { value: "urdu", label: "Urdu" },
      { value: "bengali", label: "Bengali" },
      { value: "japanese", label: "Japanese" },
      { value: "german", label: "German" },
      { value: "persian", label: "Persian/Farsi" },
      { value: "swahili", label: "Swahili" },
      { value: "somali", label: "Somali" },
      { value: "burmese", label: "Burmese" },
      { value: "amharic", label: "Amharic" },
      { value: "polish", label: "Polish" },
      { value: "italian", label: "Italian" },
      { value: "thai", label: "Thai" },
      { value: "sign_language", label: "American Sign Language (ASL)" },
      { value: "other", label: "Other" },
    ],
    maritalStatuses: [
      { value: "single", label: "Single / Never Married" },
      { value: "married", label: "Married" },
      { value: "separated", label: "Separated" },
      { value: "divorced", label: "Divorced" },
      { value: "widowed", label: "Widowed" },
      { value: "domestic_partner", label: "Domestic Partner / Civil Union" },
      { value: "cohabiting", label: "Cohabiting" },
      { value: "unknown", label: "Unknown" },
      { value: "declined", label: "Declined to Answer" },
    ],
    educationLevels: [
      { value: "no_formal_education", label: "No Formal Education" },
      { value: "less_than_high_school", label: "Less than High School" },
      { value: "some_high_school", label: "Some High School" },
      { value: "high_school", label: "High School Diploma / GED" },
      { value: "some_college", label: "Some College" },
      { value: "trade_school", label: "Trade / Vocational Certificate" },
      { value: "associates", label: "Associate's Degree" },
      { value: "bachelors", label: "Bachelor's Degree" },
      { value: "masters", label: "Master's Degree" },
      { value: "doctorate", label: "Doctorate / Professional Degree" },
      { value: "unknown", label: "Unknown" },
      { value: "declined", label: "Declined to Answer" },
    ],
    employmentStatuses: [
      { value: "employed_full_time", label: "Employed - Full Time" },
      { value: "employed_part_time", label: "Employed - Part Time" },
      { value: "not_employed", label: "Not Employed" },
      { value: "disabled_ssi", label: "Disabled - SSI" },
      { value: "self_employed", label: "Self-Employed" },
      { value: "retired", label: "Retired" },
      { value: "student", label: "Student" },
      { value: "homemaker", label: "Homemaker" },
      { value: "seasonal", label: "Seasonal / Temporary" },
      { value: "unknown", label: "Unknown" },
      { value: "declined", label: "Declined to Answer" },
    ],
    referralSources: [
      { value: "self", label: "Self-Referral" },
      { value: "sapd", label: "SAPD" },
      { value: "bcso", label: "Bexar County Sheriff's Department" },
      { value: "law_enforcement", label: "Law Enforcement (Other)" },
      { value: "family_justice_center", label: "Family Justice Center" },
      { value: "child_protective_services", label: "Child Protective Services" },
      { value: "court_advocate", label: "Court Advocate" },
      { value: "hospital", label: "Hospital" },
      { value: "crisis_response_team", label: "Crisis Response Team" },
      { value: "family_friend", label: "Family / Friend" },
      { value: "fvps_org", label: "FVPS.ORG" },
      { value: "police_other_county", label: "Police (Other County)" },
      { value: "mental_health", label: "Mental Health" },
      { value: "minister_clergy", label: "Minister / Clergy" },
      { value: "other_shelter", label: "Other Shelter" },
      { value: "phonebook", label: "Phonebook" },
      { value: "previous_client", label: "Previous Client" },
      { value: "social_services", label: "Social Services" },
      { value: "dv_hotline", label: "DV Hotline" },
      { value: "hhsc", label: "HHSC" },
      { value: "metro_health_sapd_bcso", label: "Metro Health w/ SAPD/BCSO #" },
      { value: "metro_health_no_sapd_bcso", label: "Metro Health w/o SAPD/BCSO #" },
      { value: "star_intervention_bcso", label: "STAR Intervention & Prevention-BCSO" },
      { value: "center_family_relations", label: "Center for Family Relations" },
      { value: "raices", label: "Raices" },
      { value: "bcjs_e_project", label: "BCJS - The E Project" },
      { value: "kid_share", label: "Kid Share" },
      { value: "kids_exchange", label: "Kids Exchange" },
      { value: "other", label: "Other" },
    ],
    abuseTypes: [
      { value: "physical", label: "Physical" },
      { value: "emotional", label: "Emotional / Psychological" },
      { value: "sexual", label: "Sexual" },
      { value: "economic", label: "Economic / Financial" },
      { value: "stalking", label: "Stalking" },
      { value: "trafficking", label: "Trafficking" },
      { value: "digital", label: "Digital / Technology-Facilitated" },
      { value: "verbal", label: "Verbal" },
      { value: "neglect", label: "Neglect" },
      { value: "other", label: "Other" },
    ],
    relationships: [
      { value: "spouse", label: "Spouse" },
      { value: "ex_spouse", label: "Ex-Spouse" },
      { value: "partner", label: "Partner / Boyfriend / Girlfriend" },
      { value: "ex_partner", label: "Ex-Partner" },
      { value: "parent", label: "Parent" },
      { value: "stepparent", label: "Stepparent" },
      { value: "child", label: "Child" },
      { value: "sibling", label: "Sibling" },
      { value: "other_family", label: "Other Family Member" },
      { value: "caregiver", label: "Caregiver" },
      { value: "roommate", label: "Roommate / Co-Habitant" },
      { value: "dating", label: "Dating Relationship" },
      { value: "other", label: "Other" },
      { value: "unknown", label: "Unknown" },
    ],
    housingStatuses: [
      { value: "own_or_mortgage", label: "Own or Mortgage" },
      { value: "rent", label: "Rent" },
      { value: "living_with_friends_family", label: "Living with Friends/Family" },
      { value: "homeless_shelter", label: "Homeless Shelter" },
      { value: "subsidized_housing", label: "Subsidized Housing" },
      { value: "transitional_housing_program", label: "Transitional Housing Program" },
      { value: "hotel_motel", label: "Hotel / Motel" },
      { value: "vehicle", label: "Vehicle" },
      { value: "unsheltered", label: "Unsheltered (Street, Park, etc.)" },
      { value: "other", label: "Other" },
    ],
    incomeSourceTypes: [
      { value: "employment", label: "Employment" },
      { value: "ssi_ssdi", label: "SSI / SSDI" },
      { value: "tanf", label: "TANF" },
      { value: "child_support", label: "Child Support" },
      { value: "alimony", label: "Alimony / Spousal Support" },
      { value: "unemployment", label: "Unemployment Benefits" },
      { value: "pension", label: "Pension / Retirement" },
      { value: "va_benefits", label: "VA Benefits" },
      { value: "snap", label: "SNAP / Food Stamps" },
      { value: "social_security", label: "Social Security" },
      { value: "workers_comp", label: "Workers' Compensation" },
      { value: "other", label: "Other" },
      { value: "none", label: "None" },
    ],
    familyTypes: [
      { value: "single_under_18_no_children", label: "Single Individual Under 18 (No Children)" },
      { value: "single_18_or_older_no_children", label: "Single 18 Years or Older (No Children)" },
      { value: "single_parent_18_or_older", label: "Single Parent 18 Year or Older with Children" },
      { value: "single_parent_under_18", label: "Single Parent Under Age 18" },
      { value: "two_parent_with_children", label: "Two-Parent Family HH with Children" },
      { value: "grandparents_responsible", label: "Family HH w/ Grandparents Responsible" },
      { value: "other_family_hh", label: "Other Family HH" },
      { value: "hh_type_unknown", label: "HH Type Unknown" },
    ],
    victimTypes: [
      { value: "victim_adult", label: "Victim - Adult" },
      { value: "victim_child", label: "Victim - Child" },
      { value: "batterer", label: "Batterer" },
      { value: "none", label: "None" },
    ],
    sites: [
      { value: "main", label: "Main Office" },
      { value: "satellite", label: "Satellite Office" },
      { value: "shelter_a", label: "Shelter A" },
    ],
    programs: [
      { value: "cbc_h4h_adult", label: "! CBC H4H Adult" },
      { value: "child_cbc_h4h_wings", label: "! Child CBC H4H (Wings)" },
      { value: "counseling_services_adult", label: "~ Counseling Services - Adult" },
      { value: "counseling_services_adult_h4h", label: "~ Counseling Services - Adult H4H" },
      { value: "fvps_rrh_hud", label: "~ FVPS RRH - HUD" },
      { value: "united_way_counselor", label: "~ United Way Counselor" },
      { value: "dv_court_counseling", label: "~DV Court Counseling Services" },
      { value: "parents_journey", label: "A Parent's Journey" },
      { value: "adult_education", label: "Adult Education Program" },
      { value: "bwcs", label: "BWCS" },
      { value: "celebrating_families", label: "Celebrating Families" },
      { value: "changing_behavior", label: "Changing Behavior through Choice" },
      { value: "community_integrated", label: "Community Integrated Services" },
      { value: "court_liaison_cps", label: "Court Liaison - CPS" },
      { value: "court_liaison_military", label: "Court Liaison - Military" },
      { value: "court_liaison_other", label: "Court Liaison - Other" },
      { value: "court_liaison_staffing", label: "Court Liaison - Staffing" },
      { value: "cps_2054", label: "CPS - 2054" },
      { value: "cps_liaison", label: "CPS - Liaison" },
      { value: "cs_child_individual_counseling", label: "CS-Child Individual Counseling" },
      { value: "cs_childcare", label: "CS-Childcare" },
      { value: "donation_center", label: "Donation Center" },
      { value: "fdc_counseling", label: "FDC Counseling Services" },
      { value: "fema_transitional", label: "FEMA Transitional" },
      { value: "fvpp_counseling", label: "FVPP Counseling Services" },
      { value: "fvps_rrh", label: "FVPS RRH" },
      { value: "fvps_transitional", label: "FVPS Transitional" },
      { value: "housing_support_hud_rrh", label: "Housing Support - HUD - RRH" },
      { value: "kid_share", label: "Kid Share" },
      { value: "la_paloma_transitional", label: "La Paloma Transitional" },
      { value: "legal_services", label: "Legal Services" },
      { value: "tdhca_moving_forward_rental", label: "TDHCA - Moving Forward - Rental" },
      { value: "tdhca_rental_assistance", label: "TDHCA Rental Assistance" },
      { value: "yac", label: "Youth Advisory Council (YAC)" },
    ],
    advocates: [
      { value: "melody_acevedo", label: "Melody Acevedo" },
      { value: "sonya_alvarado", label: "Sonya Alvarado" },
      { value: "jacqueline_avant", label: "Jacqueline Avant" },
      { value: "beatrice_avila", label: "Beatrice Avila" },
      { value: "mari_ayala_sandoval", label: "Mari Ayala-Sandoval" },
      { value: "rosa_benavides", label: "Rosa Benavides" },
      { value: "alberto_bustamante", label: "Alberto Bustamante" },
      { value: "christina_campos", label: "Christina Campos" },
      { value: "gaby_canavati", label: "Gaby Canavati" },
      { value: "myra_cardenas", label: "Myra Cardenas" },
      { value: "laura_carrera", label: "Laura Carrera" },
      { value: "adrianna_carrillo", label: "Adrianna Carrillo" },
      { value: "jose_castellanos", label: "Jose Castellanos" },
      { value: "melissa_chaidez", label: "Melissa Chaidez" },
      { value: "melinda_darrow", label: "Melinda Darrow" },
      { value: "amber_deffenbaugh", label: "Amber Deffenbaugh" },
      { value: "violet_espinoza", label: "Violet Espinoza" },
      { value: "victoria_esquivel", label: "Victoria Esquivel" },
      { value: "dawson_fagan", label: "Dawson Fagan" },
      { value: "katelynn_flores", label: "Katelynn Flores" },
      { value: "shanna_flores", label: "Shanna Flores" },
      { value: "gina_garcia", label: "Gina Garcia" },
      { value: "maggie_garcia", label: "Maggie Garcia" },
      { value: "savannah_garza", label: "Savannah Garza" },
      { value: "lucia_gonzalez", label: "Lucia Gonzalez" },
      { value: "sandra_gonzalez", label: "Sandra Gonzalez" },
      { value: "amber_guerra", label: "Amber Guerra" },
      { value: "lorena_guerra", label: "Lorena Guerra" },
      { value: "michelle_hamelwright", label: "Michelle Hamelwright" },
      { value: "veronica_hernandez", label: "Veronica Hernandez" },
      { value: "norma_hurtado", label: "Norma Hurtado" },
      { value: "cassaundra_latorre", label: "Cassaundra Latorre" },
      { value: "leticia_longoria", label: "Leticia Longoria" },
      { value: "lourdes_manzanares", label: "Lourdes Manzanares" },
      { value: "cailin_martin", label: "Cailin Martin" },
      { value: "alma_martinez", label: "Alma Martinez" },
      { value: "alyssa_martinez", label: "Alyssa Martinez" },
      { value: "maria_martinez", label: "Maria Martinez" },
      { value: "roxanna_matelski", label: "Roxanna Matelski" },
      { value: "jennifer_mccall", label: "Jennifer McCall" },
      { value: "priscilla_mendoza", label: "Priscilla Mendoza" },
      { value: "susan_miles", label: "Susan Miles" },
      { value: "carin_miller", label: "Carin Miller" },
      { value: "michelle_mojarro", label: "Michelle Mojarro" },
      { value: "michelle_morales_deleon", label: "Michelle Morales DeLeon" },
      { value: "kimberly_parker", label: "Kimberly Parker" },
      { value: "judy_perez", label: "Judy Perez" },
      { value: "tiffany_polk", label: "Tiffany Polk" },
      { value: "genesis_portillo", label: "Genesis Portillo" },
      { value: "natalie_pruitt", label: "Natalie Pruitt" },
      { value: "simone_raaths", label: "Simone Raaths" },
      { value: "katherine_ramirez", label: "Katherine Ramirez" },
      { value: "victoria_ramon", label: "Victoria Ramon" },
      { value: "amber_ramos", label: "Amber Ramos" },
      { value: "leslie_reyes", label: "Leslie Reyes" },
      { value: "loraine_reyna", label: "Loraine Reyna" },
      { value: "aviana_rivera", label: "Aviana Rivera" },
      { value: "bethany_robinson", label: "Bethany Robinson" },
      { value: "briana_rodriguez", label: "Briana Rodriguez" },
      { value: "jacqueline_rodriguez", label: "Jacqueline Rodriguez" },
      { value: "lola_rodriguez", label: "Lola Rodriguez" },
      { value: "rita_rodriguez", label: "Rita Rodriguez" },
      { value: "ruth_rodriguez", label: "Ruth Rodriguez" },
      { value: "tamara_rodriguez", label: "Tamara Rodriguez" },
      { value: "asusena_rosas", label: "Asusena Rosas" },
      { value: "julieta_rubio", label: "Julieta Rubio" },
      { value: "felisha_ruiz", label: "Felisha Ruiz" },
      { value: "maricela_salinas", label: "Maricela Salinas" },
      { value: "mariela_sanchez", label: "Mariela Sanchez" },
      { value: "shamsheer_sanghera", label: "Shamsheer Sanghera" },
      { value: "rachel_scheller", label: "Rachel Scheller" },
      { value: "leslie_d_schultz", label: "Leslie D. Schultz" },
      { value: "jennifer_serna", label: "Jennifer Serna" },
      { value: "ashley_sterling", label: "Ashley Sterling" },
      { value: "paige_theriot", label: "Paige Theriot" },
      { value: "kathryn_nicole_tyler", label: "Kathryn Nicole Tyler" },
      { value: "mara_valdez_morales", label: "Mara Valdez Morales" },
      { value: "anahi_veloz", label: "Anahi Veloz" },
      { value: "judi_yahn", label: "Judi Yahn" },
      { value: "ana_zavala", label: "Ana Zavala" },
      { value: "karina_zulewski", label: "Karina Zulewski" },
      { value: "alamo_tech", label: "Alamo Tech" },
      { value: "bwcs_intern", label: "BWCS Intern" },
      { value: "cbc_haven_4_hope_intern", label: "CBC-Haven 4 Hope Intern" },
      { value: "cf_intern", label: "CF Intern" },
      { value: "pj_intern", label: "PJ Intern" },
      { value: "yac_intern", label: "YAC Intern" },
      { value: "intern_yac", label: "Intern YAC" },
      { value: "childrens_contractor_1", label: "Children's Contractor #1" },
      { value: "childrens_contractor_2", label: "Children's Contractor #2" },
      { value: "childrens_contractor_3", label: "Children's Contractor #3" },
      { value: "childrens_services_intern", label: "Children's Services Intern" },
      { value: "childrens_services_intern_2", label: "Children's Services Intern #2" },
      { value: "childrens_service_intern_3", label: "Children's Service Intern #3" },
      { value: "united_way_intern_bwcs", label: "United Way Intern BWCS" },
    ],
    usStates: [
      { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
      { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
      { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
      { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
      { value: "FL", label: "Florida" }, { value: "GA", label: "Georgia" },
      { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
      { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" },
      { value: "IA", label: "Iowa" }, { value: "KS", label: "Kansas" },
      { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
      { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" },
      { value: "MA", label: "Massachusetts" }, { value: "MI", label: "Michigan" },
      { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
      { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" },
      { value: "NE", label: "Nebraska" }, { value: "NV", label: "Nevada" },
      { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
      { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" },
      { value: "NC", label: "North Carolina" }, { value: "ND", label: "North Dakota" },
      { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
      { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" },
      { value: "RI", label: "Rhode Island" }, { value: "SC", label: "South Carolina" },
      { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
      { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" },
      { value: "VT", label: "Vermont" }, { value: "VA", label: "Virginia" },
      { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
      { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" },
      { value: "DC", label: "District of Columbia" },
      { value: "PR", label: "Puerto Rico" }, { value: "GU", label: "Guam" },
      { value: "VI", label: "U.S. Virgin Islands" },
      { value: "AS", label: "American Samoa" },
      { value: "MP", label: "Northern Mariana Islands" },
    ],
  };

  // Load dropdown config — always returns defaults; API can override in the future
  const { data: dropdowns, isLoading: isLoadingDropdowns } = useQuery<DropdownConfig>({
    queryKey: ["config", "dropdowns"],
    queryFn: async () => {
      // The /config/dropdowns API returns a different shape (category-based).
      // For now, always use the comprehensive defaults. When the API dropdown
      // management UI is built, this can transform the API response and merge.
      return defaultDropdowns;
    },
    staleTime: 10 * 60 * 1000,
  });

  // Load custom fields
  const { data: customFieldsData, isLoading: isLoadingCustomFields } = useQuery<CustomField[]>({
    queryKey: ["config", "custom-fields", "intake"],
    queryFn: async () => {
      try {
        return await api.get<CustomField[]>("/config/custom-fields/intake");
      } catch {
        return [];
      }
    },
    staleTime: 10 * 60 * 1000,
  });

  const customFields = customFieldsData ?? [];

  // SSN duplicate check
  const checkSsnDuplicate = useCallback(async (ssn: string) => {
    if (!ssn || ssn.replace(/\D/g, "").length < 9) {
      setSsnDuplicateWarning(null);
      return;
    }
    try {
      const result = await api.post<{ isDuplicate: boolean; existingClientName?: string }>(
        "/clients/check-ssn",
        { ssn },
      );
      if (result.isDuplicate) {
        setSsnDuplicateWarning(
          `This SSN is already associated with ${result.existingClientName ?? "another client"}.`,
        );
      } else {
        setSsnDuplicateWarning(null);
      }
    } catch {
      setSsnDuplicateWarning(null);
    }
  }, []);

  // Zip code lookup
  const lookupZipCode = useCallback(
    async (zip: string) => {
      if (!zip || zip.length < 5) return;
      try {
        const result = await api.get<{ city: string; state: string; county: string }>(
          `/config/zip-lookup/${zip}`,
        );
        if (result.city) form.setValue("city", result.city);
        if (result.state) form.setValue("state", result.state);
        if (result.county) form.setValue("county", result.county);
      } catch {
        // ZIP lookup not available, user can fill manually
      }
    },
    [form],
  );

  // Step navigation
  const goNext = useCallback(async (): Promise<boolean> => {
    const schema = stepSchemas[stepIndex];
    if (schema) {
      const values = form.getValues();
      const result = schema.safeParse(values);
      if (!result.success) {
        // Trigger validation on the relevant fields
        const fieldNames = Object.keys(schema.shape) as Array<keyof IntakeFormData>;
        for (const field of fieldNames) {
          await form.trigger(field);
        }
        return false;
      }
    }

    if (stepIndex < totalSteps - 1) {
      setStepIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return true;
    }
    return false;
  }, [form, stepIndex, totalSteps]);

  const goBack = useCallback(() => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [stepIndex]);

  const goToStep = useCallback(
    (step: number) => {
      const idx = steps.indexOf(step);
      if (idx !== -1) setStepIndex(idx);
    },
    [steps],
  );

  function setPersonType(type: PersonType) {
    form.setValue("personType", type);
    setStepIndex(0);
  }

  // Submit mutation
  const mutation = useMutation({
    mutationFn: async (data: IntakeFormData) => {
      // Map the extended form data to the API shape
      const phone =
        data.primaryPhone === "work"
          ? data.phoneWork
          : data.primaryPhone === "mobile"
            ? data.phoneMobile
            : data.phoneHome;

      const payload = {
        personType: data.personType,
        firstName: data.firstName,
        lastName: data.lastName,
        middleName: data.middleName || undefined,
        preferredName: data.preferredName || undefined,
        dateOfBirth: data.dateOfBirth || undefined,
        ssn: data.ssn || undefined,
        gender: data.gender || undefined,
        ethnicity: data.ethnicity || undefined,
        race: data.race || undefined,
        primaryLanguage: data.primaryLanguage || undefined,
        isInterpreterNeeded: data.isInterpreterNeeded,
        phone: phone || undefined,
        email: data.email || undefined,
        address: data.address
          ? data.addressApt
            ? `${data.address}, ${data.addressApt}`
            : data.address
          : undefined,
        city: data.city || undefined,
        state: data.state || undefined,
        zipCode: data.zipCode || undefined,
        county: data.county || undefined,
        intakeDate: data.intakeDate,
        referralSource: data.referralSource || undefined,
        presentingSituation: data.presentingSituation || undefined,
        abuseTypes: data.abuseTypes?.length ? data.abuseTypes : undefined,
        relationshipToAbuser: data.relationshipToAbuser || undefined,
        isAbuseCurrent: data.isAbuseCurrent,
        abuseStartDate: data.abuseStartDate || undefined,
        hasProtectionOrder: data.hasProtectionOrder,
        protectionOrderExpiration: data.protectionOrderExpiration || undefined,
        safetyPlanCompleted: data.safetyPlanCompleted,
        dangerAssessmentScore: data.dangerAssessmentScore,
        isHighDanger: data.isHighDanger,
        hasChildren: data.hasChildren,
        numberOfChildren: data.numberOfChildren,
        isPregnant: data.isPregnant,
        hasDisability: data.hasDisability,
        disabilityDetails: data.disabilityDetails || undefined,
        isVeteran: data.isVeteran,
        employmentStatus: data.employmentStatus || undefined,
        monthlyIncome: data.monthlyIncome,
        educationLevel: data.educationLevel || undefined,
        isHomeless: data.isHomeless,
        mentalHealthDiagnosis: data.mentalHealthDiagnosis || undefined,
        isSubstanceAbuse: data.isSubstanceAbuse,
        substanceAbuseDetails: data.substanceAbuseDetails || undefined,
        isSuicidal: data.isSuicidal,
        suicidalDetails: data.suicidalDetails || undefined,
        medications: data.medications || undefined,
        photoUrl: data.photoUrl || undefined,
        bedId: data.bedId || undefined,
        expectedDepartureDate: data.expectedDepartureDate || undefined,
        parentPersonId: data.parentPersonId || undefined,
        schoolName: data.schoolName || undefined,
        gradeLevel: data.gradeLevel || undefined,
        pediatrician: data.pediatrician || undefined,
        custodyStatus: data.custodyStatus || undefined,
        siteId: data.siteId || undefined,
        programId: data.programId || undefined,
      };

      return api.post<{ id: string }>("/clients", payload);
    },
    onSuccess: (result) => {
      clearDraft();
      void queryClient.invalidateQueries({ queryKey: ["clients"] });
      void navigate(`/clients/${result.id}`);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setSubmitError(err.body.message);
      } else {
        setSubmitError("An unexpected error occurred. Please try again.");
      }
    },
  });

  const submit = useCallback(async () => {
    setSubmitError(null);
    // Validate directly with Zod to get precise error details
    const data = form.getValues();
    const result = intakeFormSchema.safeParse(data);
    if (!result.success) {
      const issues = result.error.issues;
      const details = issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ");
      console.error("Intake form Zod errors:", issues);
      console.error("Form values:", data);
      setSubmitError(`Validation failed — ${details}`);
      return;
    }
    mutation.mutate(data);
  }, [form, mutation]);

  const saveIncomplete = useCallback(async () => {
    // Validate just the minimum fields
    const values = form.getValues();
    if (!values.firstName || !values.lastName || !values.intakeDate) {
      form.setError("firstName", { message: "First name is required to save" });
      form.setError("lastName", { message: "Last name is required to save" });
      return;
    }

    setSubmitError(null);
    mutation.mutate(values);
  }, [form, mutation]);

  const clearFormDraft = useCallback(() => {
    clearDraft();
    form.reset(getDefaultValues());
    setStepIndex(0);
  }, [form]);

  return {
    form,
    currentStep,
    totalSteps,
    steps,
    canGoNext,
    canGoBack,
    goNext,
    goBack,
    goToStep,
    saveIncomplete,
    submit,
    isSubmitting: mutation.isPending,
    submitError,
    dropdowns,
    isLoadingDropdowns,
    customFields,
    isLoadingCustomFields,
    personType,
    setPersonType,
    hasDraft,
    clearFormDraft,
    ssnDuplicateWarning,
    checkSsnDuplicate,
    lookupZipCode,
  };
}
